import { useMemo, useContext } from 'react';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  KolSearchResultsSortContext,
  KolSearchResultsFilterContext,
  KolSearchResultsPageSizeContext,
  KolSearchResultsPageIndexContext,
  KolSearchTablePageContext,
  ActiveChatSessionContext,
} from './context';
import type { Context } from './interfaces.context';
import type { KolSearchTableFilters } from './interfaces.state';
import type { KolSearchResultSortKey } from './interfaces.types';
import { isFilterValid } from './utils';
import { Chat } from '@trinity/chat';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { LocationItem } from '@trinity/chat';
import { KolSearchResult } from '@trinity/chat';
import { useDebounceValue } from '@trinity/chat-components/hooks/useDebounceValue';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';

type Props = {
  children: React.ReactNode;
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.HcpSelection;
};

export const SmartRoutingTableContainer = (props: Props) => {

  const sortState = useContext(KolSearchResultsSortContext);
  const filterContext = useContext(KolSearchResultsFilterContext);
  const filters = useMemo(() => {
    const allFilters = filterContext?.state || [];
    return allFilters.filter(isFilterValid);
  }, [filterContext?.state]);

  const brandIdentifier = useActiveBrandIdentifier();
  const chat = useContext(ActiveChatSessionContext);

  const searchParams = useMemo(() => ({
    chatIdentifier: chat.identifier,
    location: props.value.location,
    brandIdentifier: brandIdentifier,
    filters,
    sort: sortState,
    initialResults: props.value.results || [],
  }), [
    chat.identifier,
    props.value.location,
    brandIdentifier,
    filters,
    sortState,
  ]);

  const enabled = useMemo(() => {
    return [
      'form-results',
    ].includes(props.value.status);
  }, [props.value.status]);

  const searchResult = useNearbyHcps(searchParams, enabled);
  const records = useMemo(() => searchResult.data?.results || [], [searchResult.data]);

  const pageSize = useContext(KolSearchResultsPageSizeContext);
  const pageIndex = useContext(KolSearchResultsPageIndexContext);

  const page = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;

    return records.slice(start, end);
  }, [
    records,
    pageIndex,
    pageSize,
  ]);

  const ctx = useMemo(() => ({
    records: page,
    total: records.length,
    showDistanceCol: true,
    requery: false,
  }), [page, records.length]);

  return (
    <KolSearchTablePageContext.Provider value={ctx}>
      {props.children}
    </KolSearchTablePageContext.Provider>
  );
};

type SearchParams = {
  chatIdentifier: string;
  location?: Pick<LocationItem, 'latitude' | 'longitude'>;
  brandIdentifier: string;
  filters: KolSearchTableFilters.State['filters'];
  sort: Context.TableSortContextData<KolSearchResultSortKey>;
  initialResults: KolSearchResult[];
};

const useNearbyHcps = (rawParams: SearchParams, enabled: boolean) => {

  const params = useDebounceValue(rawParams, 500);
  const apiClient = useChatApiClient();

  return useQuery({
    queryKey: ['nearby-hcps', params],
    queryFn: async ({ queryKey }) => {
      const { chatIdentifier, location, brandIdentifier, filters, sort } = queryKey[1] as SearchParams;

      const res = await apiClient.authenticatedClient.post<ApiResponse>(`/chat/nearby-hcps`, {
        brandIdentifier,
        chatIdentifier,
        location,
        filters,
        sort,
      });
      return res.data;
    },
    enabled,
    placeholderData: keepPreviousData,
    initialData: params.initialResults.length > 0 ? { results: params.initialResults } : undefined,
  });
};

type ApiResponse = {
  results: KolSearchResult[];
};